import { AuthContext } from '@/context/AuthContext';
import { Button, Flex, Slider, Tooltip } from '@mantine/core';
import { IconMusic, IconPiano } from '@tabler/icons-react';
import React, { useContext, useEffect, useMemo, useRef } from 'react';

interface TimelineZoomBarProps {
    zoom: number;
    zoomIntervals: number;
    onZoomChange?: (zoomLevel: number) => void;
}

const TimelineZoomBar: React.FC<TimelineZoomBarProps> = ({ zoom, zoomIntervals, onZoomChange }) => {
    const zoomLevelRef = useRef(zoom);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("This component must be a child of AuthContext.");
    }

    useEffect(() => {
        zoomLevelRef.current = zoom;
    }, [zoom]);

    const handleZoomChange = (newZoomLevel: number) => {
        if (newZoomLevel !== zoomLevelRef.current) {
            zoomLevelRef.current = newZoomLevel;

            if (onZoomChange) {
                onZoomChange(newZoomLevel);
            }
        }
    };

    const atMaxZoom = zoom === zoomIntervals - 1;

    // Generate marks programmatically
    const marks = useMemo(
        () =>
            Array.from({ length: zoomIntervals }, (_, index) => {
                const value = index + 1; // Adjust to start from 1 instead of 0
                return { value };
            }),
        [zoomIntervals]
    );

    return (
        <Flex
            direction="row"
            align={"center"}
            justify={"space-between"}
        >
            <Tooltip
                position="bottom"
                withArrow
                label="Piano Roll View"
                onClick={() => handleZoomChange(0)}
            >
                <Button
                    size="compact-xs"
                    variant='subtle'
                >
                    <IconPiano
                        display={"block"}
                        size={16}
                        color={!atMaxZoom ? "green" : "gray"}
                    />
                </Button>
            </Tooltip>

            <Slider
                value={zoom}
                onChange={handleZoomChange}
                min={0}
                max={zoomIntervals - 1}
                step={1}
                aria-label="Timeline Zoom Level"
                thumbSize={16}
                style={{
                    width: '100%',
                    paddingLeft: 12,
                    paddingRight: 12
                }}
                styles={{
                    label: {
                        top: "20px"
                    }
                }}
                label={() => "Zoom" + (!auth.beginnerTourActive ? " (Ctrl + Scroll)" : "")}
                marks={marks}
                size="sm"
            />

            <Tooltip
                position="bottom"
                withArrow
                label="Notation View"
                onClick={() => handleZoomChange(zoomIntervals - 1)}
            >
                <Button
                    size="compact-xs"
                    variant='subtle'
                >
                    <IconMusic
                        display={"block"}
                        size={16}
                        color={atMaxZoom ? "green" : "gray"}
                    />
                </Button>
            </Tooltip>
        </Flex>
    );
};

export default TimelineZoomBar;
